import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { TrainingService } from '../training.service';
import { AuthService } from '../../auth.service';
import { EmployeeService } from '../../employee/employee.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BeginCourseModalComponent } from '../begin-course-modal/begin-course-modal.component';
import { CourseDetailsModalComponent } from '../course-details-modal/course-details-modal.component';
import { ADMIN_IDS } from '../../utils/constants';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

/** Roles from Auth0 token (localStorage/profile) that see manager dashboard; others see employee view only. */
const TRAINING_MANAGER_ROLES = ['TManager'];

@Component({
  selector: 'app-training-dashboard',
  templateUrl: './training-dashboard.component.html',
  styleUrls: ['./training-dashboard.component.css']
})
export class TrainingDashboardComponent implements OnInit {
  employees: any[] = [];
  courses: any[] = [];
  loading = true;
  searchText = '';
  managerId: number;
  user: any;
  /** True when logged-in user is Admin/Training Admin (sees all employees). */
  isTrainingAdmin = false;
  /** True when showing only the logged-in employee's card (employee/trainee view). */
  isEmployeeView = false;
  notifications: any[] = [];
  unreadCount = 0;
  showNotificationsPanel = false;

  constructor(
    private trainingService: TrainingService,
    private auth: AuthService,
    private employeeService: EmployeeService,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.auth.userProfile$.subscribe((profile) => {
      this.user = profile;
      if (this.user) {
        this.managerId = this.auth.getEmployeeId();
        const empId = Number(this.managerId);
        this.isTrainingAdmin = ADMIN_IDS.includes(empId);
        this.loadDashboard();
      }
    });
  }

  loadDashboard(): void {
    this.loading = true;
    this.isEmployeeView = false;

    // 1. Admin (from ADMIN_IDS) → dashboard viewAll only
    if (this.isTrainingAdmin) {
      this.trainingService.getDashboard(null, true).subscribe(
        (response) => this.handleDashboardResponse(response),
        (error) => this.handleDashboardError(error)
      );
      return;
    }

    // 2. Manager (role from Auth0 token / localStorage in profile) → dashboard with managerId
    const role = (this.auth.role || this.user?.['https://rhv2.com.metadata']?.role || this.user?.['https://rhv2.com.role'] || '').trim();
    if (TRAINING_MANAGER_ROLES.some(r => role === r)) {
      this.trainingService.getDashboard(this.managerId, false).subscribe(
        (response) => this.handleDashboardResponse(response),
        (error) => this.handleDashboardError(error)
      );
      return;
    }

    // 3. Employee (role e.g. "User") → only enrollments?employeeId= (no dashboard API)
    this.loadEmployeeSelfView();
  }

  private handleDashboardResponse(response: any): void {
    if (response.Error) {
      this.employees = [];
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }
    const employeesList = response.Employees || [];
    this.employees = employeesList.map((emp: any) => this.mapEmployee(emp));
    this.loadCoursesAndDone();
    this.loadNotifications();
  }

  private handleDashboardError(error: any): void {
    console.error('Error loading dashboard:', error);
    this.employees = [];
    this.courses = [];
    this.loading = false;
    this.cdr.detectChanges();
  }

  private mapEmployee(emp: any): any {
    const employeeName = (emp.employee_name || '').trim();
    const trainingLevel = typeof emp.training_level === 'string' ? parseFloat(emp.training_level) : (emp.training_level || 0);
    return {
      employee_id: emp.employee_id,
      employee_name: employeeName || `Employee ${emp.employee_id}`,
      initials: (emp.initials || '').trim(),
      training_level: isNaN(trainingLevel) ? 0 : trainingLevel,
      is_training: emp.is_training || false,
      progress_pct: emp.progress_pct || 0,
      course_name: emp.course_name || null,
      courses: emp.courses || []
    };
  }

  private loadCoursesAndDone(): void {
    this.trainingService.getCourses().subscribe((coursesResponse) => {
      if (!coursesResponse.Error) {
        this.courses = coursesResponse.Courses || [];
      }
      this.loading = false;
      this.cdr.detectChanges();
    });
  }

  loadNotifications(): void {
    if (this.isEmployeeView) return;
    this.trainingService.getTrainingNotifications(this.managerId).subscribe((res) => {
      if (res && !res.Error) {
        this.notifications = res.Notifications || [];
        this.unreadCount = res.unreadCount != null ? res.unreadCount : this.notifications.filter((n: any) => !n.read_at).length;
      }
      this.cdr.detectChanges();
    });
  }

  toggleNotificationsPanel(): void {
    this.showNotificationsPanel = !this.showNotificationsPanel;
    if (this.showNotificationsPanel) {

      this.trainingService.getTrainingNotifications(this.managerId).subscribe((res) => {
        if (res && !res.Error) {
          this.notifications = res.Notifications || [];
          const unread = res.unreadCount != null ? res.unreadCount : this.notifications.filter((n: any) => !n.read_at).length;
          this.unreadCount = unread;
          if (unread > 0) {
            this.trainingService.markAllNotificationsRead().subscribe(() => {
              this.unreadCount = 0;
              this.notifications.forEach((n: any) => { n.read_at = n.read_at || new Date().toISOString(); });
              this.cdr.detectChanges();
            });
          }
        }
        this.cdr.detectChanges();
      });
    }
  }

  markNotificationViewed(n: any): void {
    if (n.read_at) return;
    this.trainingService.markNotificationRead(n.id).subscribe(() => {
      n.read_at = new Date().toISOString();
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      this.cdr.detectChanges();
    });
  }

  formatNotificationDate(d: string): string {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return diffMins + ' min ago';
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return diffHours + ' hr ago';
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return diffDays + ' day(s) ago';
    return date.toLocaleDateString();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.showNotificationsPanel && target && !target.closest('.notification-header-wrap')) {
      this.showNotificationsPanel = false;
      this.cdr.detectChanges();
    }
  }

  private loadEmployeeSelfView(): void {
    const employeeId = this.managerId;
    forkJoin({
      enrollments: this.trainingService.getEnrollments(employeeId, undefined),
      employee: this.employeeService.getOneEmployee(employeeId).pipe(catchError(() => of(null)))
    }).subscribe(
      ({ enrollments, employee }) => {
        const list = (enrollments as any).Enrollments || [];
        const empData = employee && !employee.Error ? employee : {};
        const employeeName = (empData.employee_name || '').trim();
        const trainingLevel = typeof empData.training_level_cached === 'number'
          ? empData.training_level_cached
          : parseFloat(empData.training_level_cached) || 0;
        const completed = list.filter((e: any) => e.status === 'COMPLETED');
        const coursesForBadges = completed.map((e: any) => ({
          course_id: e.course_id,
          course_name: e.course_name ? String(e.course_name).trim() : null,
          course_description: e.course_description || null,
          duration_days: e.duration_days || null,
          date_completed: e.date_completed || null,
          status: e.status
        }));
        // One card per enrollment so employee sees all courses (active and completed)
        this.employees = list.map((enrollment: any) => {
          const progressPct = parseFloat(enrollment.progress_pct) || 0;
          const courseName = enrollment.course_name ? String(enrollment.course_name).trim() : null;
          return {
            employee_id: employeeId,
            employee_name: employeeName || `Employee ${employeeId}`,
            initials: (empData.initials || '').trim(),
            training_level: isNaN(trainingLevel) ? 0 : trainingLevel,
            is_training: enrollment.status === 'ACTIVE',
            progress_pct: progressPct,
            course_name: courseName,
            courses: coursesForBadges,
            enrollmentId: enrollment.id ? String(enrollment.id) : null
          };
        });
        this.isEmployeeView = true;
        this.loadCoursesAndDone();
      },
      (error) => {
        console.error('Error loading employee view:', error);
        this.employees = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  openBeginCourseModal(employee: any): void {
    const dialogRef = this.dialog.open(BeginCourseModalComponent, {
      width: '600px',
      data: {
        employee: employee,
        courses: this.courses,
        managerId: this.managerId
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        // Reload dashboard after enrollment to show updated employee card with course progress
        this.loadDashboard();
      }
    });
  }

  /** Employee (self) view: clickable when in progress or completed. Admin/manager: clickable only when in progress. */
  isCardClickable(employee: any): boolean {
    if (this.isEmployeeView) {
      return !!(employee.course_name || (employee.courses && employee.courses.length));
    }
    return !!(employee.course_name && employee.is_training);
  }

  viewEmployeeDetail(employee: any): void {
    if (!this.isCardClickable(employee)) return;
    // Employee (self) view with one card per enrollment: navigate directly by enrollmentId
    if (this.isEmployeeView && employee.enrollmentId) {
      this.router.navigate(['/training/employee', employee.enrollmentId]);
      return;
    }
    this.trainingService.getEnrollments(employee.employee_id, undefined).subscribe(
      (response) => {
        if (response.Error || !response.Enrollments || response.Enrollments.length === 0) return;
        const list = response.Enrollments;
        const activeEnrollment = list.find((e: any) => e.status === 'ACTIVE');
        const enrollmentToOpen = activeEnrollment || (this.isEmployeeView ? list[0] : null);
        if (enrollmentToOpen && enrollmentToOpen.id) {
          this.router.navigate(['/training/employee', enrollmentToOpen.id]);
        }
      },
      (error) => console.error('Error fetching enrollment:', error)
    );
  }

  get filteredEmployees(): any[] {
    if (!this.searchText) return this.employees;
    const term = this.searchText.toLowerCase();
    return this.employees.filter(e => (e.employee_name || '').toLowerCase().includes(term));
  }

  getProgressColor(progress: number): string {
    if (progress >= 80) return '#4caf50';
    if (progress >= 50) return '#ff9800';
    return '#f44336';
  }

  viewCourseDetails(course: any): void {
    const dialogRef = this.dialog.open(CourseDetailsModalComponent, {
      width: '600px',
      maxWidth: '90vw',
      panelClass: 'course-details-panel',
      data: {
        course: course
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Modal closed
    });
  }

  formatTrainingLevel(level: number | string): string {
    if (level === null || level === undefined) {
      return '0.000';
    }
    // Handle both string and number types
    const numLevel = typeof level === 'string' ? parseFloat(level) : level;
    if (isNaN(numLevel)) {
      return '0.000';
    }
    return (numLevel < 10 ? '0' : '') + numLevel.toFixed(3);
  }

  /** Map course name to certification badge image filename (assets/images/). */
  getBadgeImagePath(courseName: string): string {
    if (!courseName || typeof courseName !== 'string') {
      return '';
    }
    const name = courseName.trim();
    const map: Record<string, string> = {
      'RFI': 'RFI.png',
      'ESSF': 'ESSF.png',
      'ESMF': 'ESMF.png',
      'CFA': 'CFA.png',
      'QAD': 'QAD.png',
      'HERS-Rater': 'HERS-Rater.png',
      'Rater': 'HERS-Rater.png',
      'HVAC-Grading': 'HVAC-Grading.png',
      'HVAC Grading': 'HVAC-Grading.png',
      'NGBS-Green-Verifier': 'NGBS-Green-Verifier.png',
      'NGBS Green Verifier': 'NGBS-Green-Verifier.png',
      'NGBS(FR)': 'NGBS(FR).png',
      'NGBS Field Rep': 'NGBS(FR).png'
    };
    const filename = map[name] || map[name.replace(/\s+/g, '-')] || null;
    if (!filename) {
      return '';
    }
    return `assets/images/${filename}`;
  }

  /**
   * Derive badges from the training level number.
   * Each digit position maps to a certification; digit value 2 = level 2 badge replaces level 1.
   *
   * Digit positions (for a number like AB.CDE):
   *   1 (tens=A):        QAD
   *   2 (ones=B):        1=RFI, 2=Rater
   *   3 (1st decimal=C): HVAC Grading
   *   4 (2nd decimal=D): 1=ESSF, 2=ESMF
   *   5 (3rd decimal=E): 1=NGBS(FR), 2=NGBS(GV)
   */
  getBadgesFromLevel(trainingLevel: number | string): { name: string, image: string }[] {
    const num = typeof trainingLevel === 'string' ? parseFloat(trainingLevel) : (trainingLevel || 0);
    if (isNaN(num)) return [];

    // Format as XX.XXX to reliably extract each digit
    const formatted = (num < 10 ? '0' : '') + num.toFixed(3);
    // formatted = "AB.CDE" — e.g. "12.120"
    const digits = formatted.replace('.', '').split('').map(d => parseInt(d, 10));
    // digits = [A, B, C, D, E]

    const DIGIT_BADGE_MAP: { level1: { name: string, image: string } | null, level2: { name: string, image: string } | null }[] = [
      { level1: { name: 'QAD',          image: 'assets/images/QAD.png' },                 level2: null },
      { level1: { name: 'RFI',          image: 'assets/images/RFI.png' },                 level2: { name: 'Rater',      image: 'assets/images/HERS-Rater.png' } },
      { level1: { name: 'HVAC Grading', image: 'assets/images/HVAC-Grading.png' },        level2: null },
      { level1: { name: 'ESSF',         image: 'assets/images/ESSF.png' },                level2: { name: 'ESMF',       image: 'assets/images/ESMF.png' } },
      { level1: { name: 'NGBS(FR)',     image: 'assets/images/NGBS(FR).png' },            level2: { name: 'NGBS(GV)',   image: 'assets/images/NGBS-Green-Verifier.png' } }
    ];

    const badges: { name: string, image: string }[] = [];
    for (let i = 0; i < digits.length && i < DIGIT_BADGE_MAP.length; i++) {
      const val = digits[i];
      if (val === 0) continue;
      const slot = DIGIT_BADGE_MAP[i];
      if (val >= 2 && slot.level2) {
        badges.push(slot.level2);
      } else if (slot.level1) {
        badges.push(slot.level1);
      }
    }
    return badges;
  }
}
